/**
 * @file Loads the two Storage artifacts a staged mapping review needs —
 * `parsed/labeled.json` (the cooked chunks) and `process/<n>.json` (the
 * proposal) — in parallel, validating only the discriminator the session
 * dispatches on (`version`, `target.kind`). Selection resolution from a
 * `ChunkRef` to its ordered, single-kind `TableChunk[]` is done per-table by
 * the session (`resolveTableChunks`), since a whole-document proposal carries
 * one selection per `TablePlan` rather than a single top-level `source.ref`.
 * Throws descriptive errors on fetch or parse failure so the review panel can
 * show them while keeping the approval decision available. Consumed by
 * `openMappingReview` in `mappingReviewSession.ts`.
 */
import { getBytes, ref } from 'firebase/storage';
import { pathForStage } from '#types';
import type { Chunk, ChunkRef, ProcessDocument, TableChunk } from '#types';
import { storage } from '../services/startStorage';

export interface MappingReviewSource {
  /** Every cooked chunk in the parsed file (table and text). */
  chunks: Chunk[];
  doc: ProcessDocument;
}

function parseJsonBytes<T>(bytes: ArrayBuffer, path: string): T {
  const text = new TextDecoder().decode(bytes);
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`Invalid JSON at ${path}`);
  }
}

/**
 * Resolves one `ChunkRef` to its ordered table chunks, mirroring the server's
 * `resolveSelection`: `scope:'chunk'` matches one chunk by `label.id`,
 * `scope:'group'` matches every chunk sharing that `chunkGroupId`, in parse
 * order. Throws if the selection is unknown or includes non-table chunks.
 */
export function resolveTableChunks(chunks: Chunk[], selection: ChunkRef): TableChunk[] {
  const members =
    selection.scope === 'chunk'
      ? chunks.filter((c) => c.label.id === selection.id)
      : chunks.filter((c) => c.label.chunkGroupId === selection.id);
  if (members.length === 0) {
    const available = chunks.map((c) => c.label.id).join(', ');
    throw new Error(
      `Unknown ${selection.scope} "${selection.id}" in parsed file. Available: [${available}]`,
    );
  }
  const tables = members.filter((c): c is TableChunk => c.kind === 'table');
  if (tables.length !== members.length) {
    throw new Error(
      `Selection "${selection.id}" includes non-table chunks; mapping review needs a table selection.`,
    );
  }
  return tables;
}

export async function loadMappingReviewSource(
  fileFolder: string,
  processIndex: number,
): Promise<MappingReviewSource> {
  const labeledPath = pathForStage(fileFolder, 'parsed', { name: 'labeled' });
  const processPath = pathForStage(fileFolder, 'process', { index: processIndex });
  const [labeledBytes, processBytes] = await Promise.all([
    getBytes(ref(storage, labeledPath)),
    getBytes(ref(storage, processPath)),
  ]);
  const allChunks = parseJsonBytes<Chunk[]>(labeledBytes, labeledPath);
  if (!Array.isArray(allChunks)) {
    throw new Error(`Parsed chunks at ${labeledPath} are not an array`);
  }
  const doc = parseJsonBytes<ProcessDocument>(processBytes, processPath);
  if (doc?.version !== 1 || doc.target?.kind == null) {
    throw new Error(`Process document at ${processPath} has an unexpected shape`);
  }
  return { chunks: allChunks, doc };
}
